'use client';

import React from 'react';
import { X, Crown, Check, Star, Globe } from 'lucide-react';
import { useRouter } from 'next/navigation';

interface ProUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ProUpsellModal({
  isOpen,
  onClose,
}: ProUpsellModalProps) {
  const router = useRouter();

  if (!isOpen) return null;

  const handleUpgrade = () => {
    router.push('/pricing');
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(4px)',
        padding: '1rem',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'white',
          borderRadius: '20px',
          padding: '1.5rem',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '1rem',
            right: '1rem',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(0, 0, 0, 0.05)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
          }}
          aria-label="Close"
        >
          <X size={20} color="#64748b" />
        </button>

        {/* Icon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            margin: '0 auto 1rem auto',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #8B45FF, #A569FF)',
            boxShadow: '0 8px 24px rgba(139, 69, 255, 0.3)',
          }}
        >
          <Crown size={32} color="white" />
        </div>

        {/* Title */}
        <h2
          style={{
            margin: '0 0 0.5rem 0',
            fontSize: 'clamp(1.375rem, 4vw, 1.75rem)',
            fontWeight: 800,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #8B45FF, #A569FF)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Unlock Amaya Pro
        </h2>

        {/* Subtitle */}
        <p
          style={{
            margin: '0 0 1.25rem 0',
            fontSize: '0.9375rem',
            color: '#64748b',
            textAlign: 'center',
            lineHeight: '1.5',
          }}
        >
          Unlock global storytelling and language learning with <strong>Amaya Pro</strong>
        </p>

        {/* Features */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.65rem',
            marginBottom: '1.25rem',
            padding: '1rem',
            background: 'linear-gradient(135deg, rgba(139, 69, 255, 0.05), rgba(165, 105, 255, 0.05))',
            borderRadius: '14px',
            border: '1px solid rgba(139, 69, 255, 0.2)',
          }}
        >
          {[
            { icon: '🌍', text: '13 Languages: English, Spanish, French, German, Italian, Portuguese, Dutch, Japanese, Korean, Mandarin, Arabic, Polish, Lithuanian' },
            { icon: '🎓', text: 'Bilingual Learn Mode: Side-by-side text + dual audio for language learning' },
            { icon: '🔄', text: 'Translate existing stories to any language instantly' },
            { icon: '🎤', text: 'Custom AI voices for personalized narration' },
            { icon: '⚡', text: 'Priority generation (2× faster queue)' },
            { icon: '💎', text: '100 credits per month + everything in Amaya Plus' },
          ].map((feature, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.65rem',
              }}
            >
              <div
                style={{
                  flexShrink: 0,
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #8B45FF, #A569FF)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Check size={13} color="white" strokeWidth={3} />
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: '0.875rem',
                  color: '#2c3e50',
                  lineHeight: '1.45',
                }}
              >
                <span style={{ marginRight: '0.375rem' }}>{feature.icon}</span>
                {feature.text}
              </p>
            </div>
          ))}
        </div>

        {/* Plan Badge */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '1.25rem',
          }}
        >
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.4rem',
              padding: '0.625rem',
              background: 'linear-gradient(135deg, rgba(139, 69, 255, 0.1), rgba(165, 105, 255, 0.1))',
              border: '2px solid rgba(139, 69, 255, 0.4)',
              borderRadius: '10px',
            }}
          >
            <Crown size={16} color="#8B45FF" fill="#8B45FF" />
            <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: '#4A148C' }}>
              Amaya Pro - $49.99/month
            </span>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.625rem',
          }}
        >
          <button
            onClick={handleUpgrade}
            style={{
              width: '100%',
              padding: '0.875rem 1.5rem',
              fontSize: '1rem',
              fontWeight: 700,
              color: 'white',
              background: 'linear-gradient(135deg, #8B45FF, #A569FF)',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: '0 4px 12px rgba(139, 69, 255, 0.4)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(139, 69, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 69, 255, 0.4)';
            }}
          >
            Upgrade to Pro
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              padding: '0.75rem 1.5rem',
              fontSize: '0.875rem',
              fontWeight: 600,
              color: '#64748b',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#2c3e50';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#64748b';
            }}
          >
            Maybe Later
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
}

